import { useMemo } from 'react';
import { Box, Checkbox, Column, Row, Tooltip, Typography } from '@tempmee/react';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Divider, Pressable } from '~/commonComponents';
import { IJob } from '~/domain/Jobs';
import { IUser } from '~/models';
import { ShiftListSelectionMode } from './enums';
import { ShiftItem } from './ShiftItem';
import './style.scss';

interface IProps {
  candidate: IUser;
  checkedJobs: Record<string, boolean>;
  isMultipleSelection: boolean;
  jobs: IJob[];
  handleToggleOption: (jobId?: string) => void;
}

export const ShiftList = ({
  candidate,
  checkedJobs,
  isMultipleSelection,
  jobs = [],
  handleToggleOption,
}: IProps) => {
  const columns = useMemo(
    () => [
      {
        title: isMultipleSelection ? 'Select All' : 'Select',
        style: {
          maxWidth: '10%',
        },
        render: isMultipleSelection
          ? () => {
              return (
                <Pressable onPress={() => handleToggleOption()}>
                  <Row gap={1} align="center" justify="center">
                    <Checkbox
                      checked={Object.keys(checkedJobs).length === jobs.length}
                      style={{ pointerEvents: 'none' }}
                    />
                    <Typography as="span" bold color={colors.primary[500]} size="body_large" textAlign="center">
                      Select All
                    </Typography>
                  </Row>
                </Pressable>
              );
            }
          : undefined,
      },
      {
        title: 'Shift Date',
      },
      {
        title: 'Profession',
      },
      {
        title: 'Hours',
      },
      {
        title: 'Hourly Rate',
      },
      {
        title: 'Total Pay',
        render: () => {
          return (
            <Column align="center">
              <Row justify="center">
                <Typography as="span" bold color={colors.primary[500]} size="body_large" textAlign="center">
                  Total Pay
                </Typography>
                <Tooltip
                  content={
                    isMultipleSelection
                      ? 'Total Pay is the total payment owed for this shift, including professional pay, GoTu service fee, and convenience fee if applicable.'
                      : 'Total Pay is the total payment owed for this shift, including professional pay, GoTu service fee, employee fee, and any other applicable fees and/or overtime charges.'
                  }
                >
                  <SolidInformationCircleIcon color={colors.neutral[300]} />
                </Tooltip>
              </Row>
            </Column>
          );
        },
      },
      {
        title: 'View Application',
        style: {
          maxWidth: '10%',
        },
      },
    ],
    [checkedJobs, handleToggleOption, isMultipleSelection, jobs.length],
  );

  return (
    <Column>
      <Row align="center">
        {columns.map(({ title, render, style }, i) => (
          <Box key={i} style={{ flex: 1, ...style }}>
            {render ? (
              render()
            ) : (
              <Typography as="span" bold color={colors.primary[500]} size="body_large" textAlign="center">
                {title}
              </Typography>
            )}
          </Box>
        ))}
      </Row>
      <Divider color={colors.neutral[50]} height={1} />
      <Column gap={2}>
        {jobs.map((job) => (
          <ShiftItem
            key={job.id}
            candidate={candidate}
            isChecked={checkedJobs[job.id]}
            job={job}
            mode={
              isMultipleSelection ? ShiftListSelectionMode.Multiple : ShiftListSelectionMode.Single
            }
            handleToggleOption={handleToggleOption}
          />
        ))}
      </Column>
    </Column>
  );
};
